// =============================================================================
// Barra de caja del POS. La sesión de caja vive cruzando recargas (turno del
// cajero), así que se consulta de la BD, no de estado efímero. Permite abrir
// (monto inicial) y cerrar (arqueo ciego: el cajero cuenta sin ver el esperado).
// Expone el cash_session_id activo al POS para adjuntarlo a cada venta.
// Requiere conexión para abrir/cerrar (RPC); offline se opera sin sesión nueva.
// =============================================================================
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { cash, ApiError } from "../lib/api";
import { money } from "../lib/utils";

export interface OpenSession { id: string; opening_amount: number; opened_at: string }

export function useCashSession(branchId: string) {
  return useQuery({
    queryKey: ["cash_session", branchId],
    queryFn: async () => {
      const { data } = await supabase.from("cash_sessions")
        .select("id, opening_amount, opened_at")
        .eq("branch_id", branchId).eq("status", "open")
        .order("opened_at", { ascending: false }).limit(1).maybeSingle();
      return (data as OpenSession | null) ?? null;
    },
  });
}

export function CashBar({ branchId, session }: { branchId: string; session: OpenSession | null }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<null | "open" | "close">(null);
  const [amount, setAmount] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => cash.open(branchId, parseFloat(amount) || 0),
    onSuccess: () => { reset(); void qc.invalidateQueries({ queryKey: ["cash_session", branchId] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const close = useMutation({
    mutationFn: () => cash.close(session!.id, parseFloat(amount) || 0),
    onSuccess: (s) => {
      const r = s as unknown as { difference: number };
      reset();
      setMsg(`Caja cerrada. Diferencia: ${money(r.difference ?? 0)}`);
      void qc.invalidateQueries({ queryKey: ["cash_session", branchId] });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  function reset() { setMode(null); setAmount(""); }

  if (!navigator.onLine) {
    return <Bar tone="offline">Caja: operando offline (abrir/cerrar requiere conexión)</Bar>;
  }

  if (!session) {
    return (
      <Bar tone="warn">
        <span>Caja cerrada.</span>
        {mode !== "open"
          ? <button onClick={() => setMode("open")} className="btn btn-primary py-1 text-xs">Abrir caja</button>
          : <span className="flex items-center gap-2">
              <input autoFocus type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Monto inicial"
                className="w-32 rounded border px-2 py-1 text-sm" />
              <button onClick={() => open.mutate()} disabled={open.isPending} className="btn btn-primary py-1 text-xs">Confirmar</button>
              <button onClick={reset} className="text-xs text-grafito-400">cancelar</button>
            </span>}
        {msg && <span className="text-xs text-red-600">{msg}</span>}
      </Bar>
    );
  }

  return (
    <Bar tone="ok">
      <span>Caja abierta · inicial {money(session.opening_amount)}</span>
      {mode !== "close"
        ? <button onClick={() => setMode("close")} className="btn btn-ghost py-1 text-xs">Cerrar / arqueo</button>
        : <span className="flex items-center gap-2">
            <input autoFocus type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Efectivo contado"
              className="w-36 rounded border px-2 py-1 text-sm" />
            <button onClick={() => close.mutate()} disabled={close.isPending} className="btn btn-primary py-1 text-xs">Cerrar caja</button>
            <button onClick={reset} className="text-xs text-grafito-400">cancelar</button>
          </span>}
      {msg && <span className="text-xs text-grafito-700">{msg}</span>}
    </Bar>
  );
}

function Bar({ tone, children }: { tone: "ok" | "warn" | "offline"; children: React.ReactNode }) {
  const bg = tone === "ok" ? "bg-green-50 text-green-800" : tone === "warn" ? "bg-amber-50 text-amber-800" : "bg-offline/10 text-offline";
  return <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-lg px-4 py-2 text-sm ${bg}`}>{children}</div>;
}
